import { FunctionComponent, useState } from "react"
import { useNavigate, useSearchParams } from "react-router-dom"
import LogoFull from "../components/logo-full"
import {
  useCompleteOnboardingMutation,
  useLoginUserPlainMutation,
} from "../app/services/meseeknaApiV2"
import { useTypedDispatch } from "../app/store"
import { setRole, updateOrganization } from "../features/auth/authSlice"
import { loginUser } from "../features/auth/authThunks"
import { Organization } from "../features/auth/types"
import zxcvbn from "zxcvbn"

const OnboardingSimple: FunctionComponent = () => {
  const navigate = useNavigate()
  const dispatch = useTypedDispatch()
  const [searchParams] = useSearchParams()

  const [completeOnboarding] = useCompleteOnboardingMutation()
  const [loginUserPlain] = useLoginUserPlainMutation()

  const [password, setPassword] = useState("")
  const [confirmPassword, setConfirmPassword] = useState("")
  const [passwordStrength, setPasswordStrength] = useState(0)
  const [passwordFeedback, setPasswordFeedback] = useState("")
  const [error, setError] = useState("")
  const [isLoading, setIsLoading] = useState(false)

  // Get token and email from URL parameters
  const token = searchParams.get("l") || ""
  const email = searchParams.get("m") || ""

  const handlePasswordChange = (value: string) => {
    setPassword(value)
    const result = zxcvbn(value)
    setPasswordStrength(result.score)
    setPasswordFeedback(
      result.feedback.warning || result.feedback.suggestions[0] || "",
    )
  }

  const getPasswordStrengthColor = () => {
    switch (passwordStrength) {
      case 0:
        return "bg-red-500"
      case 1:
        return "bg-orange-500"
      case 2:
        return "bg-yellow-500"
      case 3:
        return "bg-blue-500"
      case 4:
        return "bg-green-500"
      default:
        return "bg-gray-200"
    }
  }

  const validatePassword = () => {
    if (password.length < 8) {
      setError("Password must be at least 8 characters long")
      return false
    }

    if (passwordStrength < 3) {
      setError("Please choose a stronger password")
      return false
    }

    if (password !== confirmPassword) {
      setError("Passwords do not match")
      return false
    }

    return true
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError("")

    if (!validatePassword()) {
      return
    }

    setIsLoading(true)
    try {
      // Check if we have the necessary data
      if (!token || !email) {
        setError(
          "Missing required information. Please use the link from your email.",
        )
        setIsLoading(false)
        return
      }

      // Look for known error patterns in the backend logs
      console.log(
        "Completing onboarding with token (partial): " +
          token.substring(0, 5) +
          "..." +
          token.substring(token.length - 5),
      )

      const response = await completeOnboarding({
        token,
        password,
        email,
      }).unwrap()

      if (response.code === 200) {
        // Login the user and update Redux store
        const loginResponse = await loginUserPlain({
          username: email,
          password,
        }).unwrap()

        if (loginResponse.data?.[0]) {
          const userData = loginResponse.data[0]

          // Use the loginUser thunk to handle user authentication
          dispatch(loginUser(userData))

          // Create organization data from user data
          const organizationData: Organization = {
            id: userData.organizationId?.toString() || "",
            name: userData.organizationName || "",
            organizationName: userData.organizationName || "",
            role: userData.role?.toString() || "",
            status: "success",
            bodyMessage: "",
            fields: [],
          }

          // Set organization and role data
          dispatch(updateOrganization(organizationData))
          dispatch(setRole({ roleId: userData.role, name: userData.roleName }))

          // Navigate to dashboard instead of setup page
          navigate("/dashboard/simulations")
        } else {
          setError("Failed to log in after password setup")
        }
      } else {
        setError(response.message || "Failed to complete setup")
      }
    } catch (err) {
      const error = err as { data?: { message?: string } }
      console.error("Error during onboarding:", err)
      setError(
        error.data?.message ||
          "Your setup link has expired or has already been used. Please contact your administrator for a new invitation.",
      )
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <div className="relative bg-base-white w-full h-[100dvh] flex flex-col pt-[100px] px-8 pb-12 box-border items-center justify-start">
      <main className="w-[460px] flex flex-col items-center justify-start gap-[32px] text-left text-sm text-gray-900 font-text-md-regular1">
        <div className="self-stretch flex flex-col items-center justify-start gap-[24px] text-center font-text-md-regular">
          <LogoFull
            dimensions="/v3/assets/meseekna-logo@2x.png"
            logoWrapWidth="189px"
            logoWrapHeight="31.5px"
            image1Width="188.61px"
            image1Height="30.02px"
          />
          <div className="self-stretch flex flex-col items-start justify-start gap-[12px]">
            <h1 className="m-0 self-stretch relative text-2xl leading-[48px] font-semibold font-inherit">
              Welcome to Meseekna
            </h1>
            <div className="self-stretch relative text-base leading-[28px] font-text-md-regular1 text-gray-600">
              Please create a strong password to complete your account setup.
            </div>
          </div>
        </div>

        <form
          onSubmit={handleSubmit}
          className="self-stretch flex flex-col gap-6">
          {error && (
            <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded relative">
              <ul className="pl-4">
                <li>{error}</li>
              </ul>
            </div>
          )}

          <div>
            <label
              htmlFor="password"
              className="block text-sm font-medium text-gray-700 mb-1">
              Password
            </label>
            <input
              type="password"
              id="password"
              value={password}
              onChange={(e) => handlePasswordChange(e.target.value)}
              className="box-border w-full rounded-lg bg-base-white shadow-[0px_1px_2px_rgba(16,_24,_40,_0.05)] py-2.5 px-3.5 text-base text-gray-500 border border-gray-300"
              required
            />
            {password && (
              <div className="mt-2">
                <div className="h-2 w-full bg-gray-200 rounded-full">
                  <div
                    className={`h-full rounded-full transition-all ${getPasswordStrengthColor()}`}
                    style={{
                      width: `${(passwordStrength + 1) * 20}%`,
                    }}
                  />
                </div>
                {passwordFeedback && (
                  <p className="mt-1 text-sm text-gray-500">
                    {passwordFeedback}
                  </p>
                )}
              </div>
            )}
          </div>

          <div>
            <label
              htmlFor="confirmPassword"
              className="block text-sm font-medium text-gray-700 mb-1">
              Confirm Password
            </label>
            <input
              type="password"
              id="confirmPassword"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              className="box-border w-full rounded-lg bg-base-white shadow-[0px_1px_2px_rgba(16,_24,_40,_0.05)] py-2.5 px-3.5 text-base text-gray-500 border border-gray-300"
              required
            />
          </div>

          <button
            type="submit"
            disabled={isLoading}
            className={`cursor-pointer w-full flex justify-center py-2.5 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white ${
              isLoading
                ? "bg-gray-400 cursor-not-allowed"
                : "bg-primary-600 hover:bg-primary-700"
            }`}>
            {isLoading ? (
              <div className="flex items-center gap-2">
                <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                Setting Password...
              </div>
            ) : (
              "Set Password & Continue"
            )}
          </button>
        </form>
      </main>
    </div>
  )
}

export default OnboardingSimple
